import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  syncCustomerData,
  getCustomerAddresses,
  getStorePurchases,
  updateStorePurchase,
  getAllFreeServiceClaims,
} from "../../../api/api";
import styles from "./CustomerDetails.module.css";

const CustomerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer } = location.state || {};
  const [addresses, setAddresses] = useState([]);
  const [storePurchases, setStorePurchases] = useState([]);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [freeServiceClaims, setFreeServiceClaims] = useState([]);
  // Stringers can view customer details but not change anything
  const isReadOnly = localStorage.getItem("adminRole") === "stringer";

  const VALID_STORES = ["Marina Store", "Dubai Hills Store", "Jumeirah Store"];

  useEffect(() => {
    const token = localStorage.getItem("adminToken");

    if (!token) {
      navigate("/login", {
        state: { error: "Session expired. Please log in again." },
      });
      return;
    }

    if (!customer) {
      navigate("/dashboard/manage-customer");
      return;
    }

    // Sync customer data
    const syncCustomer = async () => {
      try {
        await syncCustomerData(customer.id, token);
        console.log("Customer data synced");
      } catch (err) {
        console.error("Failed to sync customer:", err);
      }
    };

    syncCustomer();
  }, [navigate, customer]);

  // Fetch customer addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (customer?.id) {
        try {
          const response = await getCustomerAddresses(customer.id);
          setAddresses(response.data || []);
        } catch (error) {
          console.error("Error fetching addresses:", error);
        }
      }
    };

    fetchAddresses();
  }, [customer?.id]);

  // Fetch in-store purchases
  useEffect(() => {
    const fetchStorePurchases = async () => {
      if (customer?.id) {
        try {
          const response = await getStorePurchases(customer.id);
          setStorePurchases(response.data?.purchases || []);
        } catch (error) {
          console.error("Error fetching store purchases:", error);
        }
      }
    };

    fetchStorePurchases();
  }, [customer?.id]);

  // Fetch free stringing credits per racket
  useEffect(() => {
    const fetchFreeServiceClaims = async () => {
      if (customer?.id) {
        try {
          const data = await getAllFreeServiceClaims(customer.id);
          setFreeServiceClaims(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error("Error fetching free service claims:", error);
        }
      }
    };

    fetchFreeServiceClaims();
  }, [customer?.id]);

  const handleEditClick = (purchase) => {
    setEditingReceipt(purchase.receipt_number);
    setSaveError("");
    // purchase.purchase_date is already a plain "YYYY-MM-DD HH:MM:SS" string from the backend.
    // Slice the first 10 chars for the date input — no Date object, no timezone shift.
    const localDate = purchase.purchase_date
      ? purchase.purchase_date.substring(0, 10)
      : "";
    setEditForm({
      receipt_number: purchase.receipt_number,
      store_order_number: purchase.store_order_number,
      store_name: purchase.store_name,
      purchase_date: localDate,
      products: purchase.products.map((p) => ({ ...p })),
    });
  };

  const handleCancelEdit = () => {
    setEditingReceipt(null);
    setEditForm(null);
    setSaveError("");
  };

  const handleFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProductChange = (index, field, value) => {
    setEditForm((prev) => {
      const products = prev.products.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      );
      return { ...prev, products };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      // Send purchase_date as plain YYYY-MM-DD. The backend uses MySQL CONCAT to merge
      // the new date with the existing stored time, so the time is never touched.
      await updateStorePurchase(customer.id, editingReceipt, editForm);
      // Refresh the list
      const response = await getStorePurchases(customer.id);
      setStorePurchases(response.data?.purchases || []);
      setEditingReceipt(null);
      setEditForm(null);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to save. Please try again.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      if (typeof dateStr === "string") {
        const datePart = dateStr.split(" ")[0];
        if (datePart && datePart.includes("-")) {
          const parts = datePart.split("-").map(Number);
          if (parts.length === 3 && parts.every(p => !isNaN(p))) {
            const [yr, mo, dy] = parts;
            const d = new Date(yr, mo - 1, dy);
            if (!isNaN(d.getTime())) {
              return d.toLocaleDateString("en-AE", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
            }
          }
        }
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-AE", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    } catch (e) {
      console.error("formatDate error:", e);
    }
    return "N/A";
  };

  const calcTotal = (products) =>
    (products || [])
      .reduce((sum, p) => sum + parseFloat(p.price_per_racket || 0) * parseInt(p.quantity || 1), 0)
      .toFixed(2);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleViewTicketClick = () => {
    navigate("/dashboard/ticket-details", {
      state: {
        customerId: customer.id,
        customerName: customer.name,
        customerEmail: customer.email,
        customerPhone: customer.phone,
      },
    });
  };
  const handleAddPurchaseClick = () => {
    navigate(`/dashboard/add-purchases/${customer.id}`, {
      state: { customerName: customer.name },
    });
  };

  const handleViewServiceDetailsClick = () => {
    navigate(`/dashboard/our-services/${customer.id}`, {
      state: {
        customerId: customer.id,
        customerName: customer.name,
        customerEmail: customer.email,
        customerPhone: customer.phone,
      },
    });
  };

  return (
    <div className={styles.customerDetails}>
      <div className={styles.buttonContainer}>
        <h2>Customer Details</h2>
        <button className={styles.backButton} onClick={handleBackClick}>
          Back
        </button>
      </div>
      {!isReadOnly && (
      <table className={styles.detailsTable}>
        <thead>
          <tr>
            <th>Customer Name</th>
            <th>Service Details</th>
            <th>Raised Ticket</th>
            <th>Add In-Store Purchase</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{customer?.name || "N/A"}</td>
            <td>
              <button
                className={`${styles.actionButton} ${styles.viewServiceDetailsButton}`}
                onClick={handleViewServiceDetailsClick}
              >
                View Service Details
              </button>
            </td>
            <td>
              <button
                className={`${styles.actionButton} ${styles.viewTicketButton}`}
                onClick={handleViewTicketClick}
              >
                Raised Ticket
              </button>
            </td>
            <td>
              <button
                className={`${styles.actionButton} ${styles.addPurchaseButton}`}
                onClick={handleAddPurchaseClick}
              >
                Add In-Store Purchase
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      )}

      {/* Customer Information Cards */}
      <div className={styles.customerInfoSection}>
        <h3 className={styles.sectionTitle}>Customer Information</h3>
        
        <div className={styles.cardsContainer}>
          {/* Basic Info Card */}
          <div className={styles.infoCard}>
            <h4 className={styles.cardTitle}>Basic Details</h4>
            <div className={styles.cardContent}>
              <div className={styles.infoRow}>
                <span className={styles.label}>Name:</span>
                <span className={styles.value}>{customer?.name || "N/A"}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Email:</span>
                <span className={styles.value}>{customer?.email || "N/A"}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Phone:</span>
                <span className={styles.value}>{customer?.phone || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Addresses Card */}
          <div className={styles.infoCard}>
            <h4 className={styles.cardTitle}>Addresses</h4>
            <div className={styles.cardContent}>
              {addresses.length > 0 ? (
                addresses.map((address, index) => (
                  <div key={address.address_id || index} className={styles.addressItem}>
                    <div className={styles.addressHeader}>
                      <span className={styles.addressType}>
                        {address.address_type || "Address"}
                      </span>
                      {address.is_default === 1 && (
                        <span className={styles.defaultBadge}>Default</span>
                      )}
                    </div>
                    <div className={styles.addressDetails}>
                      {address.address_line && <p>{address.address_line}</p>}
                      {address.area && <p>{address.area}</p>}
                      {address.landmark && <p>Landmark: {address.landmark}</p>}
                      <p>
                        {[address.city, address.state, address.pincode]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                      {address.country && <p>{address.country}</p>}
                    </div>
                    {index < addresses.length - 1 && <hr className={styles.divider} />}
                  </div>
                ))
              ) : (
                <p className={styles.noData}>No addresses found</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Free Stringing Section */}
      <div className={styles.storePurchasesSection}>
        <h3 className={styles.sectionTitle}>Free Stringing</h3>
        {freeServiceClaims.length === 0 ? (
          <p className={styles.noData}>No free stringing credits found</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.storePurchasesTable}>
              <thead>
                <tr>
                  <th>Racket</th>
                  <th>Total</th>
                  <th>Used</th>
                  <th>Remaining</th>
                  <th>Valid Until</th>
                </tr>
              </thead>
              <tbody>
                {freeServiceClaims.map((claim) => (
                  <tr key={claim.claim_id}>
                    <td>{claim.racket_name || "N/A"}</td>
                    <td>{claim.total_free_services}</td>
                    <td>{claim.used_services}</td>
                    <td>
                      <strong>{claim.remaining_services}</strong>
                    </td>
                    <td>{formatDate(claim.valid_until)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* In-Store Purchases Section */}
      <div className={styles.storePurchasesSection}>
        <h3 className={styles.sectionTitle}>In-Store Purchases</h3>
        {storePurchases.length === 0 ? (
          <p className={styles.noData}>No in-store purchases found</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.storePurchasesTable}>
              <thead>
                <tr>
                  <th>Receipt No</th>
                  <th>Store Order No</th>
                  <th>Store</th>
                  <th>Purchase Date</th>
                  <th>Products</th>
                  <th>Total (AED)</th>
                  {!isReadOnly && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {storePurchases.map((purchase) => {
                  const isEditing = editingReceipt !== null && editingReceipt === purchase.receipt_number;
                  return (
                    <tr key={purchase.receipt_number || `no-receipt-${purchase.store_order_number || Math.random()}`}>
                      {isEditing ? (
                        <>
                          <td>
                            <input
                              className={styles.editInput}
                              value={editForm.receipt_number}
                              onChange={(e) => handleFormChange("receipt_number", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className={styles.editInput}
                              value={editForm.store_order_number}
                              onChange={(e) => handleFormChange("store_order_number", e.target.value)}
                            />
                          </td>
                          <td>
                            <select
                              className={styles.editInput}
                              value={editForm.store_name}
                              onChange={(e) => handleFormChange("store_name", e.target.value)}
                            >
                              {VALID_STORES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="date"
                              className={styles.editInput}
                              value={editForm.purchase_date}
                              onChange={(e) => handleFormChange("purchase_date", e.target.value)}
                            />
                          </td>
                          <td>
                            <div className={styles.productEditList}>
                              {editForm.products.map((prod, idx) => (
                                <div key={idx} className={styles.productEditRow}>
                                  <div className={styles.productEditField}>
                                    <label>SKU</label>
                                    <input
                                      className={styles.editInput}
                                      value={prod.product_id}
                                      onChange={(e) => handleProductChange(idx, "product_id", e.target.value)}
                                    />
                                  </div>
                                  <div className={styles.productEditField}>
                                    <label>Name</label>
                                    <input
                                      className={styles.editInput}
                                      value={prod.product_name}
                                      onChange={(e) => handleProductChange(idx, "product_name", e.target.value)}
                                    />
                                  </div>
                                  <div className={styles.productEditField}>
                                    <label>Price/Racket</label>
                                    <input
                                      type="number"
                                      className={styles.editInput}
                                      value={prod.price_per_racket}
                                      onChange={(e) => handleProductChange(idx, "price_per_racket", e.target.value)}
                                    />
                                  </div>
                                  <div className={styles.productEditField}>
                                    <label>Qty</label>
                                    <input
                                      className={`${styles.editInput} ${styles.editInputQty}`}
                                      value={prod.quantity}
                                      readOnly
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>
                            {editForm.products
                              .reduce(
                                (sum, p) =>
                                  sum +
                                  parseFloat(p.price_per_racket || 0) *
                                    parseInt(p.quantity || 1),
                                0
                              )
                              .toFixed(2)}
                          </td>
                          <td>
                            <div className={styles.actionButtons}>
                              {saveError && (
                                <p className={styles.saveError}>{saveError}</p>
                              )}
                              <button
                                className={styles.saveButton}
                                onClick={handleSave}
                                disabled={saving}
                              >
                                {saving ? "Saving..." : "Save"}
                              </button>
                              <button
                                className={styles.cancelButton}
                                onClick={handleCancelEdit}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{purchase.receipt_number || "N/A"}</td>
                          <td>{purchase.store_order_number}</td>
                          <td>{purchase.store_name}</td>
                          <td>{formatDate(purchase.purchase_date)}</td>
                          <td>
                            <div className={styles.productList}>
                              {(purchase.products || []).map((prod, idx) => (
                                <div key={idx} className={styles.productLine}>
                                  {prod.product_name}{" "}
                                  <span className={styles.productMeta}>
                                    (SKU: {prod.product_id}) × {prod.quantity} @ AED{" "}
                                    {parseFloat(prod.price_per_racket).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>AED {calcTotal(purchase.products)}</td>
                          {!isReadOnly && (
                            <td>
                              <button
                                className={styles.editReceiptButton}
                                onClick={() => handleEditClick(purchase)}
                              >
                                Edit
                              </button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;
